import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { can } from '../../utils/permissions';

const inputSx = (colors) => ({
  backgroundColor: colors.primary[400],
  color: colors.grey[100],
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[500] },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[500] },
  '& .MuiSvgIcon-root': { color: colors.grey[200] },
});

const labelSx = (colors) => ({
  color: colors.grey[200],
  '&.Mui-focused': { color: colors.greenAccent[400] },
});

const gridShellSx = (colors) => ({
  '& .MuiDataGrid-root': { border: 'none' },
  '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}` },
  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none', color: colors.grey[100] },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
  '& .MuiDataGrid-row': { color: colors.grey[100], '&:hover': { backgroundColor: `${colors.blueAccent[800]}55` } },
  '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${colors.grey[700]}`, backgroundColor: colors.blueAccent[700], color: colors.grey[100] },
  '& .MuiTablePagination-root': { color: colors.grey[100] },
  '& .MuiIconButton-root': { color: colors.grey[200] },
  '& .MuiDataGrid-toolbarContainer .MuiButton-text': { color: `${colors.grey[100]} !important` },
});


const SurveyResponsesReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [trips, setTrips] = useState([]);
  const [guides, setGuides] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [selectedGuide, setSelectedGuide] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Load trips and guides on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [tripsRes, guidesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_APILINK}/api/admin/survey-reports/trips-with-feedback`, { headers: { token } }),
          fetch(`${process.env.REACT_APP_APILINK}/guides?page=1&limit=500&sortBy=u.id&sortOrder=ASC&filter={}`, { headers: { token } }),
        ]);
        const tripsData = await tripsRes.json();
        const guidesData = await guidesRes.json();
        if (tripsData.success) setTrips(tripsData.data || []);
        if (guidesData.success) setGuides(guidesData.guides || []);
      } catch (e) {
        console.error(e);
      }
    };
    loadFilters();
  }, [token]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedTrip) {
      const [countryId, departureDate] = selectedTrip.split('|');
      params.set('countryId', countryId);
      params.set('departureDate', departureDate);
    }
    if (selectedGuide) params.set('guideId', selectedGuide);
    return params.toString();
  }, [selectedTrip, selectedGuide]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams(buildQuery());
      params.set('page', page);
      params.set('limit', pageSize);
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/survey-reports/all-responses?${params.toString()}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setRows(data.data || []);
        setTotalCount(data.pagination?.total || 0);
      } else {
        setError(data.message || 'Failed to load responses');
        setRows([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(e.message || 'Network error');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, token, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [buildQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const qs = buildQuery();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/survey-reports/all-responses/export-excel?${qs}`,
        { headers: { token } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.message || `Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'survey-responses.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const selectedTripLabel = (() => {
    if (!selectedTrip) return '';
    const [countryId, departureDate] = selectedTrip.split('|');
    const t = trips.find((x) => String(x.countryId) === countryId && x.departureDate === departureDate);
    return t ? `${t.destination} — ${t.departureDate}` : selectedTrip;
  })();

  const columns = [
    { field: 'passenger', headerName: 'Passenger', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'destination', headerName: 'Destination', width: 160 },
    { field: 'departureDate', headerName: 'Departure', width: 110 },
    { field: 'guide', headerName: 'Guide', width: 150 },
    {
      field: 'avg_rating',
      headerName: 'Avg Rating',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (p) => (p.value != null ? `${p.value} ★` : '—'),
    },
    { field: 'submittedAt', headerName: 'Submitted At', width: 150 },
    {
      field: 'actions',
      headerName: 'Detail',
      width: 90,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (p) => (
        <Button
          size="small"
          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          onClick={() => navigate(`/userfeedback/${p.row.feedback_id}`)}
          sx={{ color: colors.greenAccent[400], textTransform: 'none' }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header title="Survey Responses" subtitle="All passenger survey responses with filters and Excel export" />

      {/* Filters */}
      <Card sx={{ mb: 3, background: colors.primary[400], border: `1px solid ${colors.grey[700]}`, borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: colors.grey[100], mb: 2 }}>Filters</Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={5} md={4}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel shrink sx={labelSx(colors)}>Trip</InputLabel>
                <Select
                  label="Trip"
                  displayEmpty
                  value={selectedTrip}
                  onChange={(e) => setSelectedTrip(e.target.value)}
                  sx={inputSx(colors)}
                  renderValue={(v) => v === '' ? <Typography component="span" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>All trips</Typography> : selectedTripLabel}
                >
                  <MenuItem value="">All trips</MenuItem>
                  {trips.map((t) => (
                    <MenuItem key={`${t.countryId}|${t.departureDate}`} value={`${t.countryId}|${t.departureDate}`}>
                      {t.destination} — {t.departureDate}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5} md={4}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel shrink sx={labelSx(colors)}>Guide</InputLabel>
                <Select
                  label="Guide"
                  displayEmpty
                  value={selectedGuide}
                  onChange={(e) => setSelectedGuide(e.target.value)}
                  sx={inputSx(colors)}
                  renderValue={(v) => v === '' ? <Typography component="span" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>All guides</Typography> : guides.find((g) => String(g.id) === v) ? `${guides.find((g) => String(g.id) === v).name} ${guides.find((g) => String(g.id) === v).surname}` : v}
                >
                  <MenuItem value="">All guides</MenuItem>
                  {guides.map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {g.name} {g.surname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} md="auto">
              {can('reports.survey_responses.export') && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={exporting || loading}
                  sx={{
                    px: 3, py: 1,
                    color: colors.grey[100],
                    borderColor: colors.grey[500],
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: `${colors.greenAccent[900]}22` },
                  }}
                >
                  Export Excel
                </Button>
              )}
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: colors.grey[500], mt: 1.5, display: 'block' }}>
            {totalCount} response{totalCount !== 1 ? 's' : ''} found
          </Typography>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${colors.grey[700]}`, overflow: 'hidden', backgroundColor: colors.primary[400] }}>
        <Box height={500} sx={gridShellSx(colors)}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.feedback_id}
            loading={loading}
            disableRowSelectionOnClick
            paginationMode="server"
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalCount}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(p) => setPage(p + 1)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            localeText={{ noRowsLabel: 'No survey responses found.' }}
            sx={{ border: 'none', '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default SurveyResponsesReport;
